import { useEffect, useRef } from "react";
import { useRouter } from "next/router";

import { Product } from "@/api/product-api";
import { isDiscontinued } from "@/components/tactsuit/PriceDisplay";
import { useCurrency } from "@/hooks/useCurrency";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";
import { useLocale } from "@/store/locale-store";
import { ProductUtil } from "@/utils/ProductUtil";
import { isSoldOutLimitedEdition } from "@/libs/product/limited-edition";

/** 가격 없는 제품의 PDP 에 직접 들어왔을 때 보낼 곳 — 제품 목록. */
export const UNPRICED_PRODUCT_REDIRECT = "/tactsuit/";

/**
 * 이 국가·통화에서 **가격이 없는 제품**의 상세 페이지에 직접 들어오면 제품 목록으로 보낸다.
 *
 * 판매 목록에서는 숨기지만(가격 판정 `ProductUtil.getProductPrice`), URL 로 바로 들어오면 가격 칸이 빈
 * 상세 페이지가 뜬다(prod 2026-10-01: 미국 `tactglove-dk2-bundle`). 장바구니·결제는 이미 막혀 있어(#5886)
 * 팔리지는 않지만, 살 수 없는 화면을 보여 줄 이유가 없다.
 *
 * ★**클라이언트에서** 판정한다 — 리다이렉트는 **화면이 쓰는 국가·flag** 기준이어야 하는데, 서버 렌더의 값은
 *   화면과 같다는 보장이 없다(서버 국가 = 쿠키 → IP, 화면 = 쿠키 → URL 언어 · flag 는 서버·화면이 따로 조회).
 *   그래서 페이지가 잠깐 보였다가 이동한다. ⚠️ 이유는 CDN 캐시가 아니다 — 이 페이지는 국가별 카탈로그를
 *   받아 원래 국가에 따라 응답이 다르고 캐시 헤더도 없다(#5905 R1).
 * ★국가(`""` → 쿠키 값)와 GE flag(로딩 중 `null`)가 **둘 다 확정된 뒤**에만 판정한다. 로딩 창의 임시
 *   통화(USD)로 판정하면 EUR/GBP 에만 가격이 있는 제품을 잘못 내보낼 수 있다.
 * - **품절된 한정판**(`isSoldOutLimitedEdition`, EXOSHOCK 번들)도 같은 곳으로 보낸다 — 재입고가 없어 「Sold Out」 +
 *   재입고 알림 화면이 거짓 안내가 된다. 판매 목록에서도 같은 판정으로 빠진다(`isPricedForListing`).
 * - 단종 제품은 내보내지 않는다 — 가격 칸에 「단종」 표시가 나가는 기존 동작(`isDiscontinued`)을 지킨다.
 */
export function useRedirectWhenUnpriced(product: Product | null | undefined) {
  const router = useRouter();
  const { countryCode } = useLocale();
  const currency = useCurrency();
  const flag = useIsFeatureOn(FeatureFlagName.GlobalEEnabled);
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current || !product) return;
    if (!countryCode || flag === null) return;
    const soldOutLimited = isSoldOutLimitedEdition(product);
    if (!soldOutLimited) {
      if (isDiscontinued(product.slug)) return;
      if (ProductUtil.getProductPrice({ currency, countryCode, product })) {
        return;
      }
    }
    redirected.current = true;
    console.warn(
      soldOutLimited
        ? `[PDP] limited edition sold out: ${product.slug} → ${UNPRICED_PRODUCT_REDIRECT}`
        : `[PDP] no ${currency} price for ${product.slug} (country=${countryCode}) → ${UNPRICED_PRODUCT_REDIRECT}`,
    );
    router.replace(UNPRICED_PRODUCT_REDIRECT);
  }, [product, countryCode, currency, flag, router]);
}
